import debug from "debug";
import { mount, unmount } from "svelte";
import { toast } from "svelte-sonner";
import CartModal from "../../lib/components/modals/CartModal/CartModal.svelte";
import GuestRFQModal from "../../lib/components/modals/GuestRFQModal/GuestRFQModal.svelte";
import ToastHost from "../../lib/components/ToastHost/ToastHost.svelte";
import { createRFQData } from "../../lib/helpers/createRFQData";

const log = debug("app:cartHandler");

interface MountedComponent {
  instance: ReturnType<typeof mount>;
  target: HTMLElement;
}

interface EventData {
  event: string;
  [key: string]: unknown;
}

interface AddToCartData extends EventData {
  event: "addToCart";
  bomParts?: {
    data: Array<{
      id: number;
      partNumber: string;
      qty: number;
      each?: number;
      total?: number;
      note?: string;
      [key: string]: any;
    }>;
    total?: any[];
  };
  contents?: string;
}

interface SendRFQData {
  cart: any;
  user: any;
  notes?: string;
  designId?: string;
  isRetry?: boolean; // Flag to prevent infinite retry loops
  skipSavePrompt?: boolean; // When true, send without design ID and do not show save prompt (e.g. user already chose "No")
  quoteType?: string[] | null;
}

/**
 * Handler for managing cart-related events and logic.
 * Handles add to cart, view cart, and send RFQ operations.
 * @category Components
 * @subcategory SvelteUI
 */
export class CartHandler {
  private experience: any;
  private eventHub: any;
  private mountedComponent: MountedComponent | null = null;
  private mountedGuestModal: MountedComponent | null = null;
  private mountedToastHost: MountedComponent | null = null;
  private cartData: any = null;

  /**
   * Creates an instance of CartHandler.
   * @param experience - The experience object containing eventHub and other context.
   */
  constructor(experience: any) {
    this.experience = experience;
    this.eventHub = experience.eventHub;

    this.mountToastHost();
    this.setupEventListeners();
    log("CartHandler initialized");
  }

  private mountToastHost(): void {
    if (this.mountedToastHost) return;

    const instance = mount(ToastHost, {
      target: document.body,
      props: {},
    });

    this.mountedToastHost = {
      instance,
      target: document.body,
    };
  }

  private unmountToastHost(): void {
    if (!this.mountedToastHost) return;
    unmount(this.mountedToastHost.instance);
    this.mountedToastHost = null;
  }

  /**
   * Sets up event listeners for cart-related events.
   */
  private setupEventListeners(): void {
    // Listen for addToCart event to add items to cart
    this.eventHub.on("addToCart", (data: AddToCartData) => {
      log("Received addToCart event", data);
      this.handleAddToCart(data);
    });

    // Listen for viewCart event to mount CartModal
    this.eventHub.on("viewCart", (data: EventData) => {
      log("Received viewCart event, mounting CartModal", data);
      this.mountCartModal(data);
    });

    // Listen for sendRFQ event to send RFQ request
    this.eventHub.on("sendRFQ", (data: SendRFQData) => {
      log("Received sendRFQ event", data);
      this.handleSendRFQ(data);
    });

    // Listen for cart:updated event to persist cart changes (deletions, quantity changes)
    this.eventHub.on("cart:updated", (cartData: any) => {
      log("Received cart:updated event", cartData);
      this.cartData = cartData;
    });

    // Listen for newProject event to clear stale design ID from cart
    this.eventHub.on("newProject", (data: EventData) => {
      log("Received newProject event, clearing cart design ID");
      if (this.cartData) {
        this.cartData.designId = null;
        log("Cleared design ID from cart for new project");
      }
    });

    // Listen for openGuestRFQModal event to mount GuestRFQModal
    this.eventHub.on("openGuestRFQModal", (data: any) => {
      log("Received openGuestRFQModal event, mounting GuestRFQModal", data);
      this.mountGuestRFQModal(data);
    });

    // Listen for sendRFQ:guest event to send guest RFQ request
    this.eventHub.on("sendRFQ:guest", (data: any) => {
      log("Received sendRFQ:guest event", data);
      this.handleSendGuestRFQ(data);
    });
  }

  /**
   * Handles adding items to the cart.
   * Transforms BOM parts data into cart format and stores it.
   * @param data - The add to cart event data containing bomParts.
   */
  private handleAddToCart(data: AddToCartData): void {
    try {
      const bomParts = data.bomParts;
      if (!bomParts || !bomParts.data || bomParts.data.length == 0) {
        log("No BOM parts data in addToCart event");
        toast.warning("No items available to add to cart.", {
          id: "cart-toast",
        });
        return;
      }

      // Get current design ID (projectUuid is set when a design is open/saved)
      const designId =
        this.experience?.dataVault?.currentDesignId ||
        this.experience?.designManager?.currentDesignId ||
        this.experience?.projectUuid ||
        (typeof window !== "undefined"
          ? window.currentProjectUuid
          : undefined) ||
        null;

      // Transform BOM parts to cart format
      const cartItems = bomParts.data.map((part, index) => ({
        id: part.id || index + 1,
        partNumber: part.partNumber || "",
        qty: part.qty || 1,
        each: part.each || 0,
        total: part.total || (part.each || 0) * (part.qty || 1),
        note: part.note || "",
        // Preserve additional properties for RFQ
        name: part.name || "",
        length: part.length || "",
        length_unit: part.length_unit || "",
        color: part.color || "",
        ...part, // Include any other additional fields
      }));

      // Create cart data structure matching CartModal expectations
      this.cartData = {
        data: cartItems,
        header: {
          ID: "id",
          "Part Number": "partNumber",
          QTY: "qty",
          Each: "each",
          Total: "total",
          Note: "note",
        },
        total: bomParts.total || [
          "Estimated (USD)",
          this.calculateCartTotal(cartItems),
          "",
        ],
        designId: designId,
        // Store original contents if provided
        contents: data.contents,
      };

      log("Cart data updated:", this.cartData);

      // Emit event to notify that cart was updated
      this.eventHub.emit("cart:updated", this.cartData);
      toast.success("Items added to cart", { id: "cart-toast" });
    } catch (error: any) {
      log("Error handling addToCart:", error);
      console.error("Error adding to cart:", error);
      toast.error("Failed to add items to cart.", { id: "cart-toast" });
    }
  }

  /**
   * Calculates the total price of all items in the cart.
   * @param items - Array of cart items.
   * @returns The total price.
   */
  private calculateCartTotal(items: any[]): number {
    return items.reduce((sum, item) => {
      const total = item.total || (item.each || 0) * (item.qty || 1);
      return sum + total;
    }, 0);
  }

  /**
   * Mounts the CartModal component.
   * @param data - Data to pass to the component.
   */
  private mountCartModal(data: EventData): void {
    // Unmount existing modal if present
    if (this.mountedComponent) {
      this.unmountCartModal();
    }

    // Mount directly to body - Dialog.Portal will render its content to body anyway
    const container = document.body;

    // Get user data from experience
    const user = this.experience?.dataVault?.user || {};

    // Use current cart data or empty cart
    const cart = this.cartData || {
      data: [],
      header: {
        ID: "id",
        "Part Number": "partNumber",
        QTY: "qty",
        Each: "each",
        Total: "total",
        Note: "note",
      },
      total: ["Estimated (USD)", 0, ""],
    };

    // Mount the Svelte component using Svelte 5 API
    // The component uses bind:open internally, so we pass open: true to show it initially
    const instance = mount(CartModal, {
      target: container,
      props: {
        open: true,
        experience: this.experience,
        user: user,
        cart: cart,
        data: data,
      },
    });

    this.mountedComponent = {
      instance,
      target: container,
    };

    log("CartModal mounted");
  }

  /**
   * Unmounts the CartModal component.
   */
  private unmountCartModal(): void {
    if (this.mountedComponent) {
      unmount(this.mountedComponent.instance);
      // Don't try to remove body, only remove if it's a custom container
      if (
        this.mountedComponent.target &&
        this.mountedComponent.target !== document.body &&
        this.mountedComponent.target.parentNode
      ) {
        this.mountedComponent.target.remove();
      }
      this.mountedComponent = null;
      log("CartModal unmounted");
    }
  }

  /**
   * Handles sending RFQ request to the API.
   * @param data - The send RFQ data containing cart, user, notes, and optional designId.
   */
  private async handleSendRFQ(data: SendRFQData): Promise<void> {
    const {
      cart,
      user,
      notes,
      designId,
      isRetry = false,
      skipSavePrompt = false,
      quoteType = [],
    } = data;
    const baseApiUrl =
      this.experience?.config?.baseApiUrl ||
      "https://layoutxmw-api.xmicrowave.com/api/v1";

    try {
      // Force refresh token before sending RFQ to ensure it's never stale
      const token = await this.getAuthToken(true);
      if (!token) {
        log(
          "No authentication token available for RFQ - token refresh may have failed",
        );
        // Instead of saying user is logged out, prompt them to log in again
        this.emitRFQComplete(
          false,
          "TOKEN_REFRESH_FAILED:Please log in again to send an RFQ.",
        );
        return;
      }

      // Get the design ID from various sources
      let candidateDesignId =
        designId ||
        cart?.designId ||
        this.experience?.dataVault?.currentDesignId ||
        this.experience?.projectUuid ||
        (typeof window !== "undefined" ? window.currentProjectUuid : undefined);

      // Validate the design ID exists in the user's designs (saved in database). Only ask to save when we don't have a valid saved design.
      const designIdMatches = (design: any, id: string) => {
        if (!id) return false;
        const sid = String(id);
        const dId = design.uuid ?? design.id ?? design._id ?? design.designId;
        return dId != null && String(dId) === sid;
      };
      let validatedDesignId = "";
      if (candidateDesignId) {
        const dataVault = this.experience?.dataVault;

        if (dataVault) {
          // Fetch fresh designs list to ensure we have the latest data
          try {
            log(
              "Fetching user's designs to validate design ID:",
              candidateDesignId,
            );
            await dataVault.fetchDesigns();

            const designs = dataVault.designs || [];
            log(`Checking design ID against ${designs.length} user designs`);

            const designExists = designs.some((d: any) =>
              designIdMatches(d, candidateDesignId),
            );

            if (designExists) {
              log(
                `Design ID ${candidateDesignId} validated (exists in DB) - no save prompt`,
              );
              validatedDesignId = candidateDesignId;
            } else {
              log(`Design ID ${candidateDesignId} not found in user's designs`);
              validatedDesignId = "";
            }
          } catch (error) {
            log("Error fetching designs for validation:", error);
            const cachedDesigns = dataVault.designs || [];
            const designExists = cachedDesigns.some((d: any) =>
              designIdMatches(d, candidateDesignId),
            );
            if (designExists) {
              validatedDesignId = candidateDesignId;
            } else {
              validatedDesignId = "";
            }
          }
        } else {
          log("DataVault not available - sending RFQ without design ID");
          validatedDesignId = "";
        }
      }

      // Only ask to save when we have a local ID but it is not in the database. Already-saved designs (validatedDesignId set) skip this.
      if (candidateDesignId && !validatedDesignId && !skipSavePrompt) {
        this.unmountCartModal();
        this.eventHub.emit("cartModal:close");
        this.experience?.eventHub?.emit("modalInfo", {
          title: "Save design?",
          message:
            "Do you want to share your design with X-Microwave for quoting purposes?",
          actions: [
            {
              label: "Yes",
              callback: () => {
                this.eventHub.once("designSaved", (e: { uuid?: string }) => {
                  const savedUuid = e?.uuid ?? "";
                  log(
                    "Design saved for RFQ, sending with design ID:",
                    savedUuid,
                  );
                  this.handleSendRFQ({
                    cart: cart || this.cartData || { data: [] },
                    user: user || this.experience?.dataVault?.user || {},
                    notes: notes ?? "",
                    designId: savedUuid,
                    quoteType,
                  });
                });
                this.eventHub.emit("saveProject");
              },
            },
            {
              label: "No",
              callback: () => {
                log("User declined save, sending RFQ without design ID");
                this.handleSendRFQ({
                  cart: cart || this.cartData || { data: [] },
                  user: user || this.experience?.dataVault?.user || {},
                  notes: notes ?? "",
                  designId: "",
                  skipSavePrompt: true,
                  quoteType,
                });
              },
            },
          ],
          autoClose: false,
        });
        return;
      }

      // Create RFQ payload using the helper function with validated design ID
      const payload = createRFQData({
        user: user || this.experience?.dataVault?.user || {},
        cart: cart || this.cartData || { data: [] },
        notes: notes || "",
        designId: validatedDesignId,
        quoteType,
      });

      log("Sending RFQ request:", {
        url: `${baseApiUrl}/rfq`,
        payload: payload,
      });

      const response = await fetch(`${baseApiUrl}/rfq`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);
        log("RFQ sent successfully:", responseData);
        this.emitRFQComplete(true, responseData);

        // Optionally clear cart after successful RFQ
        // this.cartData = null;
      } else {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        let errorMessage = errorText || response.statusText;

        // Try to parse error JSON
        let errorObj: any = null;
        try {
          errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorObj.message || errorText;
        } catch {
          // Not JSON, use as-is
        }

        log("RFQ send failed:", {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
        });

        // Handle 401 Unauthorized - try to refresh token and retry
        if (response.status === 401 && !isRetry) {
          log("401 Unauthorized - Attempting token refresh and retry");
          const eventHub = this.experience?.eventHub;

          if (eventHub) {
            try {
              // Request token refresh via event
              const newToken = await new Promise<string | null>((resolve) => {
                const timeout = setTimeout(() => {
                  log("Token refresh request timeout");
                  eventHub.off("tokenRefreshResponse", tokenHandler);
                  resolve(null);
                }, 10000); // 10 second timeout

                const tokenHandler = (token: string | null) => {
                  clearTimeout(timeout);
                  eventHub.off("tokenRefreshResponse", tokenHandler);
                  resolve(token || null);
                };

                eventHub.once("tokenRefreshResponse", tokenHandler);
                eventHub.emit("tokenRefreshRequest");
              });

              if (newToken) {
                log("Token refreshed successfully, retrying RFQ request");

                // Retry the request with new token
                const retryResponse = await fetch(`${baseApiUrl}/rfq`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${newToken}`,
                  },
                  body: JSON.stringify(payload),
                });

                if (retryResponse.ok) {
                  const retryResponseData = await retryResponse
                    .json()
                    .catch(() => null);
                  log(
                    "RFQ sent successfully after token refresh:",
                    retryResponseData,
                  );
                  this.emitRFQComplete(true, retryResponseData);
                  return; // Success, exit early
                } else {
                  log("Retry failed with status:", retryResponse.status);
                  // If retry still fails with 401, prompt user to log in again
                  if (retryResponse.status === 401) {
                    this.emitRFQComplete(
                      false,
                      "TOKEN_REFRESH_FAILED:Your session has expired. Please log in again to send an RFQ.",
                    );
                  } else {
                    const retryErrorText = await retryResponse
                      .text()
                      .catch(() => retryResponse.statusText);
                    this.emitRFQComplete(
                      false,
                      retryErrorText || "Authentication failed",
                    );
                  }
                  return;
                }
              } else {
                log("Token refresh failed - prompting user to log in again");
                // Token refresh failed, prompt user to log in again instead of saying logged out
                this.emitRFQComplete(
                  false,
                  "TOKEN_REFRESH_FAILED:Your session has expired. Please log in again to send an RFQ.",
                );
                return;
              }
            } catch (refreshError) {
              log("Error during token refresh:", refreshError);
              // If refresh itself fails, prompt user to log in again
              this.emitRFQComplete(
                false,
                "TOKEN_REFRESH_FAILED:Please log in again to send an RFQ.",
              );
              return;
            }
          }
        }

        // If error is about locked design and we have a designId, retry without designId
        // Only retry once to prevent infinite loops
        if (
          !isRetry &&
          response.status === 500 &&
          (errorMessage?.toLowerCase().includes("locked design") ||
            errorMessage
              ?.toLowerCase()
              .includes("error occured while saving locked design")) &&
          payload.designId
        ) {
          log("Design is locked, retrying RFQ without designId");
          return this.handleSendRFQ({
            cart: cart || this.cartData || { data: [] },
            user: user || this.experience?.dataVault?.user || {},
            notes: notes || "",
            designId: "", // Retry with empty designId
            isRetry: true, // Mark as retry to prevent further retries
            quoteType,
          });
        }

        console.error("RFQ send failed:", {
          status: response.status,
          error: errorMessage,
        });
        this.emitRFQComplete(false, errorMessage);
      }
    } catch (error: any) {
      log("Error sending RFQ:", error);
      console.error("RFQ send error:", error);
      this.emitRFQComplete(false, error?.message || "Failed to send RFQ");
    }
  }

  /**
   * Gets the authentication token using the tokenRequest pattern.
   * @param forceRefresh - If true, forces a token refresh before returning the token.
   * @returns Promise that resolves to the token string or null.
   */
  private getAuthToken(forceRefresh: boolean = false): Promise<string | null> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        log("Token request timeout");
        this.eventHub.off("tokenResponse", tokenHandler);
        resolve(null);
      }, 10000); // 10 second timeout

      const tokenHandler = (token: string | null) => {
        clearTimeout(timeout);
        this.eventHub.off("tokenResponse", tokenHandler);
        resolve(token || null);
      };

      this.eventHub.once("tokenResponse", tokenHandler);
      this.eventHub.emit("tokenRequest", forceRefresh);
    });
  }

  /**
   * Emits a completion event for the RFQ send operation and shows the confirmation or error modal.
   * Success modal only when the RFQ was successfully sent, for all flows:
   * - Not saved (user chose No → sent without design ID)
   * - Saved on send (user chose Yes → save then send with design ID)
   * - Saved before send (design already in DB → send with design ID)
   */
  private emitRFQComplete(success: boolean, data?: any): void {
    this.eventHub.emit("sendRFQ:complete", { success, data });

    if (success) {
      this.eventHub.emit("modalInfo", {
        title: "RFQ Sent",
        message: "Your RFQ has been sent successfully.",
        actions: [{ label: "OK", callback: () => {} }],
        autoClose: false,
      });
    } else {
      const errorMessage =
        (data != null ? String(data) : "RFQ failed") || "RFQ failed";
      const isTokenRefreshFailed = errorMessage.includes(
        "TOKEN_REFRESH_FAILED:",
      );
      const isAuthError =
        isTokenRefreshFailed ||
        /authentication|login|session has expired/i.test(errorMessage);
      const displayMessage = isTokenRefreshFailed
        ? errorMessage.replace("TOKEN_REFRESH_FAILED:", "").trim()
        : isAuthError
          ? "Please log in to send an RFQ."
          : "There was an error sending your RFQ. Please try again.";
      this.eventHub.emit("modalInfo", {
        title: isAuthError ? "Login Required" : "RFQ Failed",
        message: displayMessage,
        actions: isAuthError
          ? [
              {
                label: "Log In",
                callback: () => this.eventHub.emit("signInClick"),
              },
              { label: "Cancel", callback: () => {} },
            ]
          : [{ label: "OK", callback: () => {} }],
        autoClose: false,
      });
    }
  }

  /**
   * Gets the current cart data.
   * @returns The current cart data or null.
   */
  getCartData(): any {
    return this.cartData;
  }

  /**
   * Clears the cart data.
   */
  clearCart(): void {
    this.cartData = null;
    this.eventHub.emit("cart:cleared");
    log("Cart cleared");
  }

  /**
   * Mounts the GuestRFQModal component.
   */
  private mountGuestRFQModal(data: any): void {
    if (this.mountedGuestModal) {
      this.unmountGuestModal();
    }

    const instance = mount(GuestRFQModal, {
      target: document.body,
      props: {
        open: true,
        cart: data?.cart ?? this.cartData,
        experience: this.experience,
        initialNotes: data?.notes ?? "",
        quoteType: data?.quoteType ?? [],
      },
    });

    this.mountedGuestModal = { instance, target: document.body };
    log("GuestRFQModal mounted");
  }

  /**
   * Unmounts the GuestRFQModal component.
   */
  private unmountGuestModal(): void {
    if (this.mountedGuestModal) {
      unmount(this.mountedGuestModal.instance);
      this.mountedGuestModal = null;
      log("GuestRFQModal unmounted");
    }
  }

  /**
   * Sends an RFQ for a guest user (no auth token) to POST /rfq-guest.
   */
  private async handleSendGuestRFQ(data: any): Promise<void> {
    const { user, cart, notes, xmdData, designId, saveDesign, quoteType } = data;
    const baseApiUrl =
      this.experience?.config?.baseApiUrl ??
      "https://layoutxmw-api.xmicrowave.com/api/v1";
    // const baseApiUrl = "http://localhost:3333/api/v1";

    try {
      const payload: Record<string, unknown> = {
        ...createRFQData({
          user: user ?? {},
          cart: cart ?? this.cartData ?? { data: [] },
          notes: notes ?? "",
          quoteType: quoteType ?? [],
        }),
        ...(saveDesign
          ? { saveDesign: true, designId: designId ?? "" }
          : { saveDesign: false, designId: "" }),
        // ...(saveDesign && xmdData ? { xmdData } : {}),
      };

      console.log(payload, "payload");

      log("Sending guest RFQ request to /rfq-guest", payload);

      const response = await fetch(`${baseApiUrl}/rfq/guest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);
        log("Guest RFQ sent successfully:", responseData);
        this.eventHub.emit("sendRFQ:guest:complete", {
          success: true,
          data: responseData,
        });
        this.unmountGuestModal();
      } else {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        log("Guest RFQ failed:", response.status, errorText);
        this.eventHub.emit("sendRFQ:guest:complete", {
          success: false,
          data: errorText,
        });
      }
    } catch (error: any) {
      log("Error sending guest RFQ:", error);
      this.eventHub.emit("sendRFQ:guest:complete", {
        success: false,
        data: error?.message,
      });
    }
  }

  /**
   * Cleans up the handler by removing event listeners and unmounting the component.
   */
  cleanup(): void {
    log("Cleaning up CartHandler");

    if (this.mountedComponent) {
      this.unmountCartModal();
    }

    if (this.mountedGuestModal) {
      this.unmountGuestModal();
    }

    if (this.mountedToastHost) {
      this.unmountToastHost();
    }

    // Remove event listeners
    this.eventHub.off("addToCart");
    this.eventHub.off("viewCart");
    this.eventHub.off("sendRFQ");
    this.eventHub.off("cart:updated");
    this.eventHub.off("newProject");
    this.eventHub.off("openGuestRFQModal");
    this.eventHub.off("sendRFQ:guest");

    log("CartHandler cleanup complete");
  }
}
